import React from "react"
import { twMerge } from "tailwind-merge"

import { Divider } from "../../components/shared/Layout"
import PracticeReviewsProfile from "../../components/shared/PracticeReviewsProfile"
import { ToastProvider, useToast } from "../../contexts/ToastContext"
import GraphQLProvider from "../../providers/GraphQLProvider"
import ChatWidget from "../ProProfileShared/ChatWidget"
import Footer from "../ProProfileShared/Footer"
import Navigation from "../ProProfileShared/Navigation"
import ThemeFiveNavigation from "../ProProfileThemeFive/ThemeFiveNavigation"

import BookableEventTypeDetails from "./BookableEventTypeDetails"
import BookingSection from "./BookingSection"
import ExpandableDescription from "./ExpandableDescription"
import FreeDiscoveryCallBlock from "./FreeDiscoveryCallBlock"
import LocationSection from "./LocationSection"
import MeetPractitionerSection from "./MeetPractitionerSection"

const ProProfileBookableEventTypePage = ({ practice, bookableEventType, currentUser, bookableEvents }) => {
  const toast = useToast()

  const theme = practice.theme
  const themeName = theme.name

  const bookableEventTypeHasImage = Boolean(
    bookableEventType.filestackPhoto && Object.keys(bookableEventType.filestackPhoto).length > 0
  )

  const NavigationComponent = () => {
    if (themeName === "vitality") {
      return <ThemeFiveNavigation practice={practice} theme={theme} />
    }

    if (themeName === "serenity") {
      return <Navigation practice={practice} theme={theme} color={theme.colorTextDark} />
    }

    return <Navigation practice={practice} theme={theme} color={theme.colorTextDark} />
  }

  return (
    <GraphQLProvider>
      <ToastProvider>
        <main className="bg-white md:overflow-x-hidden">
          <div className="relative">
            {/* Centered Title */}
            <div
              className="relative flex min-h-[500px] items-center justify-center text-center sm:min-h-[300px]"
              style={{ backgroundColor: theme.colorSecondary }}>
              <h1
                className={twMerge(
                  "mb-[200px] text-[56px] leading-[72px] text-gray-dark lg:text-4xl lg:leading-[48px] sm:mb-[100px]"
                )}
                style={{
                  fontFamily: theme.fontHeading,
                  fontWeight: theme.fontHeadingWeight,
                  color: theme.colorTextDark
                }}>
                {bookableEventType.name}
              </h1>
              <NavigationComponent />
            </div>

            {/* Hero Image Section with Navigation Arrows */}
            <div className="container relative mx-auto mt-[-240px] max-w-3xl px-4 sm:mt-[-120px]">
              {/* Hidden swipe arrows until multi-photo is implemented */}
              {/* <button className="absolute left-6 top-1/2 z-10 -translate-y-1/2 text-gray-dark" aria-label="Previous">
                <span className="text-4xl">&lt;</span>
              </button> */}

              {bookableEventTypeHasImage ? (
                <img
                  src={`https://cdn.filestackcontent.com/${bookableEventType.filestackPhoto.original.any}`}
                  alt={bookableEventType.name}
                  className="h-[480px] w-full rounded-lg object-cover sm:h-60"
                />
              ) : (
                <div className="flex items-center justify-center rounded-lg bg-gray-light px-4 py-32">
                  <h2 className="text-xl font-bold text-[#5f6c7b]">
                    {bookableEventType.name}
                  </h2>
                </div>
              )}

              {/* <button className="absolute right-6 top-1/2 z-10 -translate-y-1/2 text-gray-dark" aria-label="Next">
                <span className="text-4xl">&gt;</span>
              </button> */}
            </div>
          </div>

          <div className="mx-auto w-full max-w-3xl px-4 pb-32 pt-10 md:pb-10">
            {/* Reviewer Profile Section */}
            <div className="mb-8 flex justify-center">
              <PracticeReviewsProfile practice={practice} />
            </div>

            {/* Service Details Section */}
            <div className="mb-8">
              <BookableEventTypeDetails bookableEventType={bookableEventType} practice={practice} theme={theme} />
            </div>

            {/* Details Section */}
            {bookableEventType.description && (
              <>
                <div className="mb-8">
                  <ExpandableDescription
                    content={bookableEventType.description}
                    maxLines={4}
                    theme={theme}
                    title="Details"
                    charLimit={400}
                  />
                </div>

                <Divider />
              </>
            )}

            {/* Location Section */}
            {bookableEventType.kind === "in_person" && bookableEventType.location && (
              <>
                <div className="mb-8">
                  <LocationSection bookableEventType={bookableEventType} theme={theme} />
                </div>

                <Divider />
              </>
            )}

            {/* Booking Section */}
            <BookingSection bookableEventType={bookableEventType} practice={practice} bookableEvents={bookableEvents} />

            {/* Meet the Practitioner Section */}
            <div className="mb-10 mt-12">
              <MeetPractitionerSection practice={practice} />
            </div>

            {/* Free Discovery Call Block */}
            <FreeDiscoveryCallBlock practice={practice} theme={theme} />
          </div>

          <Footer practice={practice} theme={theme} />
          <ChatWidget practice={practice} currentUser={currentUser} toast={toast} />
        </main>
      </ToastProvider>
    </GraphQLProvider>
  )
}

export default ProProfileBookableEventTypePage
